/**
 * Applying to join SmartSpaza.
 *
 * The only repository that talks to the API without a session. Everything else
 * here assumes a signed-in user; an applicant has no account yet, which is the
 * whole point of applying.
 *
 * The reference is the only thing that ties the applicant to their application
 * afterwards, so it is treated as something to hold on to: the submit call
 * returns it, the status page reads it from the URL, and nothing here stores it
 * in a place the applicant cannot see.
 */

import { api } from "@/api/client";

export type ApplicationStatus =
  | "APPLICATION"
  | "PENDING_REVIEW"
  | "PENDING_DOCUMENTS"
  | "APPROVED"
  | "REJECTED";

export type DocumentKind =
  | "BUSINESS_REGISTRATION"
  | "TAX_CLEARANCE"
  | "PROOF_OF_ADDRESS"
  | "BANK_CONFIRMATION"
  | "TRADING_LICENCE"
  | "OTHER";

/** The form. Every field here is one the applicant may set — see the serializer. */
export interface ApplicationForm {
  trading_name: string;
  legal_name: string;
  registration_number: string;
  tax_number: string;
  business_type: string;
  description: string;
  phone: string;
  email: string;
  website: string;

  contact_first_name: string;
  contact_last_name: string;
  contact_email: string;
  contact_mobile: string;
  preferred_channel: string;

  address_line1: string;
  address_line2: string;
  suburb: string;
  city: string;
  province: string;
  postal_code: string;

  expected_branches: number;
  expected_tills: number;
  expected_users: number;
  needs_inventory: boolean;
  needs_procurement: boolean;
  needs_reporting: boolean;

  captcha_token?: string;
}

export interface SubmissionReceipt {
  reference: string;
  status: ApplicationStatus;
  submitted_at: string;
}

/**
 * What the status endpoint returns.
 *
 * Note what is missing: no name, no address, no contact details. The server
 * deliberately says nothing about the business behind a reference, because a
 * reference travels in forwarded emails.
 */
export interface ApplicationProgress {
  reference: string;
  status: ApplicationStatus;
  status_label: string;
  submitted_at: string | null;
  reviewed_at: string | null;
  /** Non-empty only while SmartSpaza is waiting for something. */
  information_requested: string;
}

export interface UploadedDocument {
  id: string;
  kind: DocumentKind;
  kind_label: string;
  original_name: string;
  size_bytes: number;
  created_at: string;
}

export const applicationRepository = {
  submit: (form: ApplicationForm) => api.post<SubmissionReceipt>("/applications/", form),

  progress: (reference: string) =>
    api.get<ApplicationProgress>(`/applications/${encodeURIComponent(reference)}/status/`),

  uploadDocument: (reference: string, file: File, kind: DocumentKind) => {
    const body = new FormData();
    body.append("file", file);
    body.append("kind", kind);
    return api.upload<UploadedDocument>(
      `/applications/${encodeURIComponent(reference)}/documents/`,
      body,
    );
  },

  resubmit: (reference: string, note: string) =>
    api.post<ApplicationProgress>(
      `/applications/${encodeURIComponent(reference)}/resubmit/`,
      { note },
    ),
};
